import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { tLang } from "@/contexts/PortalLanguageContext";

const CDN   = "https://pub-f22312f1902b4c5b98d85bf07b70d4e9.r2.dev";
const EMBER = "#FF6B35";
const NAVY  = "#1A1A2E";

export default function AboutFR() {
  return (
    <>
      <Helmet>
        <title>{tLang("fr", "about.meta.title", "À propos de Fyrris")}</title>
        <meta name="description" content={tLang("fr", "about.meta.description")} />
        <meta name="keywords" content="Fyrris à propos, place de marché HPB, technologie AR foyer, BBQ vie extérieure, visualisation AR vente au détail" />
        <meta property="og:title" content={tLang("fr", "about.meta.ogTitle")} />
        <meta property="og:description" content={tLang("fr", "about.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang("fr", "about.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang("fr", "about.meta.ogDescription")} />
      </Helmet>

      {/* Hero */}
      <section style={{ position: "relative", height: "65vh", overflow: "hidden" }}>
        <img
          src={`${CDN}/hero/About%20Page%20Hero.png`}
          alt="Fyrris — relier les propriétaires et les détaillants HPB grâce à la RA"
          loading="eager"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "center center",
            zIndex: 0,
          }}
        />
        <div style={{
          position: "absolute", inset: 0, zIndex: 1,
          background: "linear-gradient(to bottom, rgba(26,26,46,0.85) 0%, rgba(26,26,46,0.75) 100%)",
        }} />
        <div style={{
          position: "relative", zIndex: 10,
          height: "100%",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          textAlign: "center",
          padding: "0 1.5rem",
        }}>
          <h1 style={{
            fontSize: "clamp(2.25rem, 5vw, 4rem)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.15,
            margin: 0,
          }}>
            À propos de Fyrris
          </h1>
          <p style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: "1.125rem",
            maxWidth: "36rem",
            marginTop: "1rem",
            lineHeight: "1.75",
          }}>
            {tLang("fr", "about.mission.body", "Fyrris relie les propriétaires aux produits qu'ils aiment et aux détaillants qui les vendent — grâce à la réalité augmentée.")}
          </p>
        </div>
      </section>

      <div style={{ position: "relative", zIndex: 20, backgroundColor: NAVY }}>

        {/* Ce que nous faisons */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-gray-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-8">Ce que nous faisons</h2>
            <ul className="space-y-6 text-gray-300 max-w-3xl">
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Visualisation en réalité augmentée</strong>
                <span className="leading-relaxed">Voyez les produits tels qu'ils apparaîtraient chez vous — à l'échelle réelle, avant de visiter une salle d'exposition.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Localisateur de magasins intelligent</strong>
                <span className="leading-relaxed">Trouvez instantanément les salles d'exposition et installateurs à proximité. Chaque session RA affiche le magasin le plus proche qui porte ce produit.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">Portail détaillant</strong>
                <span className="leading-relaxed">Gérez vos fiches produits, analyses et engagement client depuis un tableau de bord intuitif.</span>
              </li>
              <li className="flex flex-col gap-1">
                <strong className="text-white text-lg">IA & automatisation 3D</strong>
                <span className="leading-relaxed">Accélérez l'intégration produits et la qualité visuelle à grande échelle — aucune compétence technique requise.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Notre histoire */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-gray-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">Notre histoire</h2>
            <div className="space-y-5 text-lg text-gray-300 leading-relaxed max-w-3xl">
              <p>
                Fyrris a été fondée par une équipe passionnée d'innovation, de design et de lien humain.
                Nous avons constaté un secteur riche en savoir-faire et en émotion, mais dépourvu
                d'outils numériques à sa hauteur.
              </p>
              <p>
                Aujourd'hui, Fyrris comble cet écart, permettant aux détaillants et fabricants
                de prospérer dans un futur axé sur la RA, tout en aidant les clients à concevoir
                leurs moments de rassemblement parfaits.
              </p>
            </div>
          </div>
        </section>

        {/* Notre vision */}
        <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.1)", marginBottom: "4rem" }} />
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24" style={{ marginTop: "4rem" }}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">Notre vision</h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl">
              Devenir la place de marché RA de référence pour l'univers du foyer, du BBQ et de la vie
              extérieure — en inspirant chacun à concevoir ses moments et à retrouver la joie d'être
              ensemble dans ses espaces extérieurs.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 sm:px-6 lg:px-8 pb-24 md:pb-32 border-t border-gray-800 pt-16">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl bg-gray-900 border border-gray-800 text-center" style={{ padding: "3rem 2rem" }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
                Vous êtes détaillant ?
              </h2>
              <p className="text-gray-300 max-w-xl mx-auto leading-relaxed text-lg">
                Rejoignez le réseau Fyrris et connectez-vous avec des acheteurs qui cherchent
                déjà des produits comme les vôtres — guidés directement vers votre magasin.
              </p>
              <div style={{ marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <Link
                  to="/fr/detaillants"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
                  style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
                >
                  Voir les forfaits détaillants →
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
